import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../context/AuthContext';
import { loginWithOTPSchema, LoginWithOTPFormData } from '../schemas/validation';
import { AppHeader } from './AppHeader';
import { LoadingScreen } from './LoadingScreen';
import './AuthForms.css';

interface OTPFormProps {
  username: string;
  password: string;
  onBack: () => void;
}

export const OTPForm: React.FC<OTPFormProps> = ({ username, password, onBack }) => {
  const { loginWithOTP, loading } = useAuth();
  const [error, setError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginWithOTPFormData>({
    resolver: zodResolver(loginWithOTPSchema),
    defaultValues: {
      username,
      password,
    },
  });

  const onSubmit = async (data: LoginWithOTPFormData) => {
    try {
      setError('');
      await loginWithOTP(data);
    } catch (err: any) {
      setError(err.message || 'OTP verification failed');
    }
  };

  return (
    <div className="auth-page">
      <AppHeader showUserInfo={false} />
      <div className="auth-form-container">
        <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
        <h2>Two-Factor Authentication</h2>
        <p className="info-text">
          We've sent a verification code to your email. Please enter it below to complete your login.
        </p>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="form-group">
          <label htmlFor="otp_code">Verification Code</label>
          <input
            id="otp_code"
            type="text"
            {...register('otp_code')}
            className={errors.otp_code ? 'error' : ''}
            placeholder="Enter 6-digit code"
            maxLength={6}
            disabled={loading}
            autoComplete="one-time-code"
          />
          {errors.otp_code && <span className="field-error">{errors.otp_code.message}</span>}
        </div>

        <div className="form-buttons">
          <button 
            type="button"
            onClick={onBack}
            disabled={loading}
            className="btn btn-outline-secondary"
          >
            Back
          </button>
          <button 
            type="submit" 
            disabled={loading}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </button>
        </div>
        </form>
      </div>
      
      {/* Loading Screen Overlay */}
      <LoadingScreen isVisible={loading} message="Verifying authentication code..." />
    </div>
  );
};